import {
  AlertCircle,
  Calendar,
  CalendarDays,
  Clock,
  Eye,
  Filter,
  Package,
  Save,
} from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Container,
  Form,
  Modal,
  Spinner,
  Toast,
  ToastContainer,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import ErrorMessageComponent from "../../../../components/ErrorMessageComponent";
import LoadingMessage from "../../../../components/LoadingMessage";
import {
  getCartChannelData,
  ProductSummary,
  useFirebaseProductSummaries,
} from "../../../../hooks/useFirebaseProductSummaries";
import {
  ApiCartAllocation,
  ApiCartHeader,
  ApiCycleAllocation,
  ApiProductAllocation,
  ChannelBreakdown,
  ChannelBreakDownProps,
  FormattedCartAllocation,
  FormattedCycleAllocation,
  FormattedPayload,
} from "../../../../redux/allocationService";
import { MenuItem } from "../../../../redux/menuSlice";
import {
  DistributeOrderRequest,
  OrderItem,
  OrderRequest,
  useDistributeOrderMutation,
  useLazyGetCartAllocationBasicPlanQuery,
  useUpdateChannelAllocationMutation,
} from "../../../../redux/services/allocationService";
import {
  convertTo24Hour,
  deepCopyChannels,
  formatDateForAPISimple,
  getChannelDisplayName,
  isSelectedDateToday,
  standardizeChannelName,
  toPascalCaseForApi,
} from "../../../../utility/inventoryUtils";
import './index.css';

export interface Cart {
  id: string;
  name: string;
  type: "fixed" | "hq" | "delivery";
  isOrderPlaced: boolean;
}

// Extended interfaces to handle optional itemType
interface ExtendedApiProductAllocation extends ApiProductAllocation {
  itemType?: string;
}

interface ExtendedApiCycleAllocation extends Omit<
  ApiCycleAllocation,
  "products"
> {
  products: ExtendedApiProductAllocation[];
}

interface ExtendedApiCartAllocation extends Omit<ApiCartAllocation, "cycles"> {
  cycles: ExtendedApiCycleAllocation[];
}

// Custom Error Component for 404
const ChannelNotFoundError: React.FC<{
  date: string;
  isTemplateMode?: boolean;
  templateSourceDate?: string;
  onRetry?: () => void;
}> = ({ date, isTemplateMode, templateSourceDate, onRetry }) => {
  return (
    <div className="custom-error-message">
      <div className="error-icon">
        <i className="fas fa-calendar-times fa-2x"></i>
      </div>
      <div className="error-title">No Production Plans Found For Today</div>
      <div className="error-subtitle">
        Please check back later or select a different date to view allocations.
      </div>
    </div>
  );
};

// Confirmation Modal Component
interface CreateOrdersConfirmationModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => void;
  cartCount: number;
  orderCount: number;
  isCreatingOrders: boolean;
}

const CreateOrdersConfirmationModal: React.FC<
  CreateOrdersConfirmationModalProps
> = ({ show, onHide, onConfirm, cartCount, orderCount, isCreatingOrders }) => {
  return (
    <Modal show={show} onHide={onHide} centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>
          <AlertCircle className="text-warning me-2" size={24} />
          Confirm Order Creation
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="text-center mb-3">
          <Package size={48} className="text-primary mb-3" />
          <h5>Are you sure you want to create orders?</h5>
          <p className="text-muted">
            This will create orders for the following:
          </p>
          <div className="bg-light p-3 rounded">
            <div className="d-flex justify-content-between mb-2">
              <span>Carts with allocations:</span>
              <strong>{cartCount}</strong>
            </div>
            <div className="d-flex justify-content-between">
              <span>Total orders to create:</span>
              <strong>{orderCount}</strong>
            </div>
          </div>
          <p className="text-danger small mt-3">
            <AlertCircle size={16} className="me-1" />
            This action cannot be undone. Orders will be created immediately.
          </p>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={onHide}
          disabled={isCreatingOrders}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          disabled={isCreatingOrders}
        >
          {isCreatingOrders ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                className="me-2"
              />
              Creating Orders...
            </>
          ) : (
            <>
              <Package size={16} className="me-2" />
              Confirm & Create Orders
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

const ChannelBreakDown: React.FC<ChannelBreakDownProps> = ({
  productionQuantities,
  selectedDate: propSelectedDate,
  templateMode = false,
  singleDateMode = false,
  templateSourceDate,
  templateSaveToDate,
  isShowingLoadFromDate = false,
  isPastDate = false,
}) => {
  const [searchProduct, setSearchProduct] = useState<string>("");
  const [tierFilter, setTierFilter] = useState<string>("all");
  const [selectedCycle, setSelectedCycle] = useState<string>("");

  // Modal state
  const [showConfirmationModal, setShowConfirmationModal] =
    useState<boolean>(false);
  const [pendingOrderData, setPendingOrderData] = useState<{
    cartCount: number;
    orderCount: number;
  }>({ cartCount: 0, orderCount: 0 });

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [internalSelectedDate, setInternalSelectedDate] = useState<string>(
    propSelectedDate || new Date().toISOString().split("T")[0],
  );

  const [orderCreationStatus, setOrderCreationStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
    ordersCreated: number;
  }>({ type: null, message: "", ordersCreated: 0 });

  // States for button management
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState<boolean>(false);
  const [saveSuccess, setSaveSuccess] = useState<boolean>(false);
  const [orderCreationSuccess, setOrderCreationSuccess] =
    useState<boolean>(false);

  const initialChannelsRef = useRef<
    Record<string, Record<string, ChannelBreakdown>>
  >({});

  const effectiveSelectedDate = propSelectedDate || internalSelectedDate;

  const gridWrapperRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);

  // RTK Query Hooks
  const [fetchCartAllocations, { data: apiData, isLoading, isError, error }] =
    useLazyGetCartAllocationBasicPlanQuery();

  const [updateChannelAllocation, { isLoading: isSaving }] =
    useUpdateChannelAllocationMutation();
  const [distributeOrder, { isLoading: isCreatingOrders }] =
    useDistributeOrderMutation();

  const restaurantId = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails.id,
  );

  const menuItems = useSelector((state: any) => state.menu.menuItems);

  const firebaseDate = useMemo(() => {
    return effectiveSelectedDate ? new Date(effectiveSelectedDate) : new Date();
  }, [effectiveSelectedDate]);

  const { data: productSummaries, loading: firebaseLoading } =
    useFirebaseProductSummaries(restaurantId, firebaseDate);

  const getUnitPriceByItemId = (itemId: string): number => {
    if (!menuItems || !Array.isArray(menuItems)) return 0;

    for (const category of menuItems) {
      if (category.items && Array.isArray(category.items)) {
        const foundItem = category.items.find(
          (item: MenuItem) =>
            item.id === itemId ||
            item.itemId === itemId ||
            item.menuItemId === itemId,
        );

        if (foundItem) {
          return foundItem.unitPrice || foundItem.price || 0;
        }
      }
    }

    return 0;
  };

  // Toast notification state
  const [showToast, setShowToast] = useState<{
    show: boolean;
    message: string;
    variant: "warning" | "danger";
  }>({
    show: false,
    message: "",
    variant: "warning",
  });

  // Fetch data when restaurantId or date changes
  useEffect(() => {
    const fetchData = async () => {
      if (!restaurantId || !effectiveSelectedDate) return;

      try {
        if (templateMode) {
          const formattedDate = formatDateForAPISimple(effectiveSelectedDate);
          fetchCartAllocations({
            restaurantId,
            date: formattedDate,
          });
        } else if (singleDateMode) {
          const formattedDate = formatDateForAPISimple(effectiveSelectedDate);
          fetchCartAllocations({
            restaurantId,
            date: formattedDate,
          });
        } else {
          const formattedDate = formatDateForAPISimple(effectiveSelectedDate);
          fetchCartAllocations({
            restaurantId,
            date: formattedDate,
          });
        }
      } catch (error) {
        console.error("Error fetching channel breakdown data:", error);
      }
    };

    fetchData();
  }, [
    restaurantId,
    effectiveSelectedDate,
    templateMode,
    singleDateMode,
    templateSourceDate,
    templateSaveToDate,
    fetchCartAllocations,
  ]);

  useEffect(() => {
    if (showToast.show) {
      const timer = setTimeout(() => {
        setShowToast({ show: false, message: "", variant: "warning" });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  const availableCycles = useMemo(() => {
    if (!apiData?.productionCycle || !Array.isArray(apiData.productionCycle))
      return [];

    return apiData.productionCycle.map((cycle) => {
      let startTime = "Invalid Date";
      let endTime = "Invalid Date";

      try {
        const startSeconds = cycle.startTime?._seconds;
        const endSeconds = cycle.endTime?._seconds;

        if (startSeconds) {
          const startDate = new Date(startSeconds * 1000);
          if (!isNaN(startDate.getTime())) {
            startTime = startDate.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            });
          }
        }

        if (endSeconds) {
          const endDate = new Date(endSeconds * 1000);
          if (!isNaN(endDate.getTime())) {
            endTime = endDate.toLocaleTimeString([], {
              hour: "2-digit",
              minute: "2-digit",
            });
          }
        }
      } catch (error) {
        console.warn("Error parsing dates for cycle:", cycle.id, error);
      }

      return {
        id: cycle.id || "",
        name: cycle.displayName || `Cycle ${cycle.id}`,
        startTime,
        endTime,
        rawStartSeconds: cycle.startTime?._seconds || 0,
        rawEndSeconds: cycle.endTime?._seconds || 0,
      };
    });
  }, [apiData]);

  useEffect(() => {
    if (availableCycles.length > 0 && !selectedCycle) {
      let earliestCycle = availableCycles[0];

      for (let i = 1; i < availableCycles.length; i++) {
        const current = availableCycles[i];

        if (
          current.startTime !== "Invalid Date" &&
          earliestCycle.startTime !== "Invalid Date"
        ) {
          const currentTime = convertTo24Hour(current.startTime);
          const earliestTime = convertTo24Hour(earliestCycle.startTime);

          if (currentTime < earliestTime) {
            earliestCycle = current;
          }
        } else {
          if (current.rawStartSeconds < earliestCycle.rawStartSeconds) {
            earliestCycle = current;
          }
        }
      }

      setSelectedCycle(earliestCycle.id);
    }
  }, [availableCycles, selectedCycle]);

  const handleCycleChange = (cycleId: string) => {
    setSelectedCycle(cycleId);
    setSaveSuccess(false);
    setOrderCreationSuccess(false);
    setHasUnsavedChanges(false);
  };

  // Get current cycle data
  const currentCycle = useMemo(() => {
    if (!apiData?.productionCycle || !selectedCycle) return null;
    return apiData.productionCycle.find((cycle) => cycle.id === selectedCycle);
  }, [selectedCycle, apiData]);

  // Check if orders are placed for a specific cart
  const isOrderPlacedForCart = (cartId: string): boolean => {
    if (!currentCycle || !apiData?.cartAllocations?.carts) return false;

    const cart = apiData.cartAllocations.carts.find(
      (c: ApiCartAllocation) => c.cartId === cartId,
    );
    if (!cart) return false;

    const cycleAllocation = cart.cycles?.find(
      (c: ApiCycleAllocation) => c.cycleId === selectedCycle,
    );
    return cycleAllocation?.isOrderPlacedForCycle === true;
  };

  // Check if any cart has isChannelAllocated true for the current cycle
  const hasChannelAllocatedCarts = useMemo(() => {
    if (!apiData?.cartAllocations?.carts || !selectedCycle) return false;

    return apiData.cartAllocations.carts.some((cart: ApiCartAllocation) => {
      const cycleAllocation = cart.cycles?.find(
        (c: ApiCycleAllocation) => c.cycleId === selectedCycle,
      );

      // Check if cart has allocations with channel data
      const hasChannelAllocations = cycleAllocation?.products?.some(
        (product: ApiProductAllocation) =>
          product.channels &&
          Object.keys(product.channels).length > 0 &&
          Object.values(product.channels).some(
            (channel: any) => channel.allocated > 0,
          ),
      );

      return hasChannelAllocations;
    });
  }, [selectedCycle, apiData]);

  const getCartAllocationsForCycle = useMemo(() => {
    const allocations: Record<string, Record<string, number>> = {};

    if (!currentCycle || !apiData?.cartAllocations?.carts || !selectedCycle)
      return allocations;

    (apiData.cartAllocations.carts || []).forEach((cart: ApiCartAllocation) => {
      const cycleAllocation = cart.cycles?.find(
        (c) => c.cycleId === selectedCycle,
      );

      if (cycleAllocation && cycleAllocation.products) {
        cycleAllocation.products.forEach((product: ApiProductAllocation) => {
          if (product && product.itemId) {
            if (!allocations[product.itemId]) {
              allocations[product.itemId] = {};
            }

            allocations[product.itemId][cart.cartId] =
              product.totalAllocated || 0;
          }
        });
      }
    });

    return allocations;
  }, [selectedCycle, currentCycle, apiData]);

  const getChannelDataForCycle = useMemo(() => {
    const channelData: Record<string, Record<string, ChannelBreakdown>> = {};

    if (!currentCycle || !apiData?.cartAllocations?.carts || !selectedCycle)
      return channelData;

    (apiData.cartAllocations.carts || []).forEach((cart: ApiCartAllocation) => {
      const cycleAllocation = cart.cycles?.find(
        (c) => c.cycleId === selectedCycle,
      );

      if (cycleAllocation && cycleAllocation.products) {
        cycleAllocation.products.forEach((product: ApiProductAllocation) => {
          if (product && product.itemId) {
            if (!channelData[product.itemId]) {
              channelData[product.itemId] = {};
            }

            if (!channelData[product.itemId][cart.cartId]) {
              channelData[product.itemId][cart.cartId] = {};
            }

            if (product.channels && typeof product.channels === "object") {
              const channels = product.channels as {
                [key: string]: { allocated: number };
              };

              channelData[product.itemId][cart.cartId] = {};

              Object.entries(channels).forEach(
                ([channelName, channelAllocation]) => {
                  if (
                    channelAllocation &&
                    typeof channelAllocation === "object"
                  ) {
                    const standardizedChannelName =
                      standardizeChannelName(channelName);
                    const allocatedValue = channelAllocation.allocated || 0;
                    // Store empty string for 0 values
                    channelData[product.itemId][cart.cartId][
                      standardizedChannelName
                    ] = allocatedValue === 0 ? "" : allocatedValue;
                  }
                },
              );
            }
          }
        });
      }
    });

    return channelData;
  }, [selectedCycle, currentCycle, apiData]);

  const getProductsForCycle = useMemo(() => {
    if (!apiData?.cartAllocations?.carts || !selectedCycle) return [];

    // Get all unique products from cart allocations for this cycle
    const productMap = new Map();

    apiData.cartAllocations.carts.forEach((cart: ExtendedApiCartAllocation) => {
      const cycleAllocation = cart.cycles?.find(
        (c: ExtendedApiCycleAllocation) => c.cycleId === selectedCycle,
      );

      if (cycleAllocation?.products) {
        cycleAllocation.products.forEach(
          (product: ExtendedApiProductAllocation) => {
            if (product && product.itemId) {
              // Use the product from cart allocation which has the correct tier
              const key = `${product.itemId}-${product.portionSize || "REGULAR"}`;
              if (!productMap.has(key)) {
                productMap.set(key, {
                  sku: product.itemId,
                  name: product.displayName || `Item ${product.itemId}`,
                  size: product.portionSize || "Regular",
                  skuId: product.skuId || "",
                  tier: (product.classFilterTag || "bronze").toLowerCase() as
                    | "bronze"
                    | "gold"
                    | "platinum",
                  itemType: product.itemType || "MenuItem", // Default to "MenuItem" if not present
                });
              }
            }
          },
        );
      }
    });

    return Array.from(productMap.values());
  }, [selectedCycle, apiData]);

  // Get carts for current cycle
  const getCartsForCycle = useMemo((): Cart[] => {
    if (!apiData?.cartAllocations?.carts || !selectedCycle) return [];

    const activeCarts = (apiData.cartAllocations.carts || []).filter(
      (cart: ExtendedApiCartAllocation) => {
        const cycleAllocation = cart.cycles?.find(
          (c: ExtendedApiCycleAllocation) => c.cycleId === selectedCycle,
        );
        return (
          cycleAllocation &&
          cycleAllocation.products?.some(
            (p: ExtendedApiProductAllocation) => (p.totalAllocated || 0) > 0,
          )
        );
      },
    );

    return activeCarts.map((cart: ExtendedApiCartAllocation) => {
      const cycleAllocation = cart.cycles?.find(
        (c: ExtendedApiCycleAllocation) => c.cycleId === selectedCycle,
      );

      let type: "fixed" | "hq" | "delivery" = "fixed";
      const cartName = (cart.cartName || "").toLowerCase();
      if (cartName.includes("hq")) {
        type = "hq";
      } else if (
        cartName.includes("delivery") ||
        cartName.includes("del") ||
        cartName.includes("mobile")
      ) {
        type = "delivery";
      }

      return {
        id: cart.cartId || "",
        name: cart.cartName || `Cart ${cart.cartId}`,
        type,
        isOrderPlaced: cycleAllocation?.isOrderPlacedForCycle === true,
      };
    });
  }, [selectedCycle, apiData]);

  const [channels, setChannels] = useState<
    Record<string, Record<string, ChannelBreakdown>>
  >(() => {
    return getChannelDataForCycle;
  });

  useEffect(() => {
    if (
      getChannelDataForCycle &&
      Object.keys(getChannelDataForCycle).length > 0
    ) {
      // Convert all 0 values to empty strings in initial data
      const formattedChannels: Record<
        string,
        Record<string, ChannelBreakdown>
      > = {};

      Object.keys(getChannelDataForCycle).forEach((sku) => {
        formattedChannels[sku] = {};
        Object.keys(getChannelDataForCycle[sku] || {}).forEach((cartId) => {
          formattedChannels[sku][cartId] = {};
          Object.keys(getChannelDataForCycle[sku][cartId] || {}).forEach(
            (channel) => {
              const value = getChannelDataForCycle[sku][cartId][channel];
              formattedChannels[sku][cartId][channel] =
                value === 0 ? "" : value;
            },
          );
        });
      });

      initialChannelsRef.current = deepCopyChannels(formattedChannels);
      setChannels(deepCopyChannels(formattedChannels));
      setHasUnsavedChanges(false);
    }
  }, [getChannelDataForCycle]);

  // Update channels when cycle changes
  useEffect(() => {
    const formattedChannels: Record<
      string,
      Record<string, ChannelBreakdown>
    > = {};

    Object.keys(getChannelDataForCycle).forEach((sku) => {
      formattedChannels[sku] = {};
      Object.keys(getChannelDataForCycle[sku] || {}).forEach((cartId) => {
        formattedChannels[sku][cartId] = {};
        Object.keys(getChannelDataForCycle[sku][cartId] || {}).forEach(
          (channel) => {
            const value = getChannelDataForCycle[sku][cartId][channel];
            formattedChannels[sku][cartId][channel] = value === 0 ? "" : value;
          },
        );
      });
    });

    setChannels(formattedChannels);
  }, [getChannelDataForCycle]);

  const shouldDisableInputs = isPastDate && singleDateMode;

  useEffect(() => {
    if (selectedCycle && availableCycles.length > 0) {
      const cycleExists = availableCycles.some(
        (cycle) => cycle.id === selectedCycle,
      );
      if (!cycleExists && availableCycles[0]) {
        let earliestCycle = availableCycles[0];

        for (let i = 1; i < availableCycles.length; i++) {
          const current = availableCycles[i];

          if (
            current.startTime !== "Invalid Date" &&
            earliestCycle.startTime !== "Invalid Date"
          ) {
            const currentTime = convertTo24Hour(current.startTime);
            const earliestTime = convertTo24Hour(earliestCycle.startTime);

            if (currentTime < earliestTime) {
              earliestCycle = current;
            }
          } else if (current.rawStartSeconds < earliestCycle.rawStartSeconds) {
            earliestCycle = current;
          }
        }

        setSelectedCycle(earliestCycle.id);
      }
    }
  }, [availableCycles, selectedCycle]);

  // Sync horizontal scroll between header and body
  useEffect(() => {
    const gridWrapper = gridWrapperRef.current;
    const header = headerRef.current;

    if (!gridWrapper || !header) return;

    const handleScroll = () => {
      const headerRow = gridWrapper.querySelector(
        ".channel-distribution-header-in-scroll",
      );
      if (headerRow) {
        headerRow.scrollLeft = gridWrapper.scrollLeft;
      }
    };

    gridWrapper.addEventListener("scroll", handleScroll);

    return () => {
      gridWrapper.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const getReservedCount = (
    sku: string,
    cartId: string,
    channelName: string,
  ): number => {
    if (!selectedCycle || !availableCycles.length) return 0;

    // Check if current cycle is the earliest one
    const earliestCycleId = availableCycles.reduce((earliest, cycle) => {
      if (
        cycle.startTime !== "Invalid Date" &&
        earliest.startTime !== "Invalid Date"
      ) {
        const currentTime = convertTo24Hour(cycle.startTime);
        const earliestTime = convertTo24Hour(earliest.startTime);
        return currentTime < earliestTime ? cycle : earliest;
      }
      return cycle.rawStartSeconds < earliest.rawStartSeconds
        ? cycle
        : earliest;
    }, availableCycles[0]).id;

    // Only show reserved count for the earliest cycle
    if (selectedCycle !== earliestCycleId) return 0;

    if (!productSummaries || firebaseLoading) return 0;

    // Find product summary for this SKU
    const productSummary = productSummaries.find(
      (summary: ProductSummary) => summary.itemId === sku,
    );

    if (!productSummary) return 0;

    // Get reserved AND cancelled counts for this cart and channel
    const channelData = getCartChannelData(productSummary, cartId, channelName);
    const reserved = channelData.reserved || 0;
    const cancelled = channelData.cancelled || 0;

    // Subtract cancelled orders from reserved count
    return Math.max(0, reserved - cancelled);
  };

  // Helper function to get channel total
  const getChannelTotal = (sku: string, cartId: string): number => {
    const channelData = channels[sku]?.[cartId];
    if (!channelData) return 0;

    return Object.values(channelData).reduce(
      (sum: number, value: string | number) => {
        const numValue = value === "" ? 0 : Number(value);
        return sum + (isNaN(numValue) ? 0 : numValue);
      },
      0,
    );
  };

  // Helper function to get allocated amount
  const getAllocatedAmount = (sku: string, cartId: string): number => {
    return getCartAllocationsForCycle[sku]?.[cartId] || 0;
  };

  // Helper function to check if allocation is valid
  const isValidAllocation = (sku: string, cartId: string): boolean => {
    const allocated = getAllocatedAmount(sku, cartId);
    if (allocated === 0) return true;
    const channelTotal = getChannelTotal(sku, cartId);
    return channelTotal === allocated;
  };

  // Helper function to update channel
  const updateChannel = (
    sku: string,
    cartId: string,
    channelName: string,
    value: string | number,
  ) => {
    // Check if orders are already placed for this cart
    if (isOrderPlacedForCart(cartId)) {
      setShowToast({
        show: true,
        message:
          "Cannot modify allocations: Orders already placed for this cart",
        variant: "warning",
      });
      return;
    }

    const allocated = getAllocatedAmount(sku, cartId);

    // Get current value (could be number or empty string)
    const currentChannelValue = channels[sku]?.[cartId]?.[channelName];
    const numericCurrentValue =
      currentChannelValue === "" ? 0 : Number(currentChannelValue) || 0;

    const numericValue = value === "" ? 0 : Number(value);

    const currentChannelTotal = getChannelTotal(sku, cartId);
    const newTotal = currentChannelTotal - numericCurrentValue + numericValue;

    if (allocated > 0 && newTotal > allocated) {
      setShowToast({
        show: true,
        message: `Cannot exceed allocated amount (${allocated}) for this product/cart`,
        variant: "danger",
      });
      return;
    }

    if (numericValue < 0) {
      setShowToast({
        show: true,
        message: "Cannot enter negative values",
        variant: "warning",
      });
      return;
    }

    setChannels((prev) => {
      const newChannels = deepCopyChannels(prev);

      if (!newChannels[sku]) {
        newChannels[sku] = {};
      }

      if (!newChannels[sku][cartId]) {
        newChannels[sku][cartId] = {};
      }

      const standardizedChannelName = standardizeChannelName(channelName);

      // Store empty string for empty input, number for other values
      if (value === "" || value === 0) {
        newChannels[sku][cartId][standardizedChannelName] = "";
      } else {
        newChannels[sku][cartId][standardizedChannelName] = numericValue;
      }

      return newChannels;
    });

    setHasUnsavedChanges(true);
    setSaveSuccess(false);
    setOrderCreationSuccess(false);
  };

  const formatChannelAllocationPayload = (): FormattedPayload | null => {
    if (!apiData?.cartAllocations?.id || !restaurantId || !selectedCycle) {
      return null;
    }

    const allocationPlanId = apiData.cartAllocations.id;
    const cartsPayload: FormattedCartAllocation[] = [];
    const formattedDate = formatDateForAPISimple(effectiveSelectedDate);

    // Build channelAllocationTotals with ALL products for ALL carts
    const productWiseChannelAllocations: Array<{
      cartId: string;
      cartName: string;
      products: Array<{
        productId: string;
        itemId: string;
        displayName: string;
        channelAllocations: Record<string, number>;
        totalAllocated: number;
        skuId: string;
      }>;
    }> = [];

    // Get ALL carts in selected cycle
    const cartsInSelectedCycle = (apiData.cartAllocations.carts || []).filter(
      (cart: ExtendedApiCartAllocation) =>
        cart.cycles?.some(
          (cycle: ExtendedApiCycleAllocation) =>
            cycle.cycleId === selectedCycle,
        ),
    );

    // Process ALL carts for channelAllocationTotals
    cartsInSelectedCycle.forEach((cart: ExtendedApiCartAllocation) => {
      if (!cart.cartId || !cart.cartName) return;

      const cartProducts: Array<{
        productId: string;
        itemId: string;
        displayName: string;
        channelAllocations: Record<string, number>;
        totalAllocated: number;
        skuId: string;
      }> = [];

      const cycleAllocation = cart.cycles?.find(
        (c: ExtendedApiCycleAllocation) => c.cycleId === selectedCycle,
      );

      if (!cycleAllocation?.products) return;

      const cartHeader = apiData?.cartsHeader?.find(
        (c: ApiCartHeader) => c.id === cart.cartId,
      );
      const supportedChannels = cartHeader?.supportedChannels || [];

      // Process ALL products for this cart
      cycleAllocation.products.forEach(
        (product: ExtendedApiProductAllocation) => {
          if (!product || !product.itemId) return;

          // SKIP CustomMenu items from save payload
          if (product.itemType === "CustomMenu") return;

          const currentChannels = channels[product.itemId]?.[cart.cartId] || {};

          const channelAllocations: Record<string, number> = {};
          let totalAllocated = 0;

          supportedChannels.forEach((channelName: string) => {
            const apiChannelName = toPascalCaseForApi(channelName);
            const rawValue =
              currentChannels[standardizeChannelName(channelName)];
            const allocatedValue = rawValue === "" ? 0 : Number(rawValue) || 0;

            channelAllocations[apiChannelName] = allocatedValue;
            totalAllocated += allocatedValue;
          });

          cartProducts.push({
            productId: product.productId || "",
            itemId: product.itemId,
            displayName: product.displayName || "",
            channelAllocations: channelAllocations,
            totalAllocated: totalAllocated,
            skuId: product.skuId || "",
          });
        },
      );

      if (cartProducts.length > 0) {
        productWiseChannelAllocations.push({
          cartId: cart.cartId,
          cartName: cart.cartName,
          products: cartProducts,
        });
      }
    });

    // Process ALL carts for carts array
    cartsInSelectedCycle.forEach((cart: ExtendedApiCartAllocation) => {
      if (!cart.cartId || !cart.cartName) return;

      const cartAllocation: FormattedCartAllocation = {
        cartId: cart.cartId,
        cartName: cart.cartName,
        cycles: [],
        isChannelAllocated: true,
      };

      const cycleAllocation = cart.cycles?.find(
        (c: ExtendedApiCycleAllocation) => c.cycleId === selectedCycle,
      );

      const cartHeader = apiData?.cartsHeader?.find(
        (c: ApiCartHeader) => c.id === cart.cartId,
      );
      const supportedChannels = cartHeader?.supportedChannels || [];

      if (cycleAllocation && cycleAllocation.products) {
        const cyclePayload: FormattedCycleAllocation = {
          cycleId: selectedCycle,
          products: [],
          isOrderPlacedForCycle: cycleAllocation.isOrderPlacedForCycle,
        };

        // Process ALL products for this cart in the cycle
        cycleAllocation.products.forEach(
          (product: ExtendedApiProductAllocation) => {
            if (product && product.itemId) {
              // SKIP CustomMenu items from save payload
              if (product.itemType === "CustomMenu") return;

              const currentChannels =
                channels[product.itemId]?.[cart.cartId] || {};

              const formattedChannels: Record<string, { allocated: number }> =
                {};
              let totalQuantity = 0;

              supportedChannels.forEach((channelName: string) => {
                const apiChannelName = toPascalCaseForApi(channelName);
                const rawValue =
                  currentChannels[standardizeChannelName(channelName)];
                const allocatedValue =
                  rawValue === "" ? 0 : Number(rawValue) || 0;

                formattedChannels[apiChannelName] = {
                  allocated: allocatedValue,
                };
                totalQuantity += allocatedValue;
              });

              const productPayload = {
                itemId: product.itemId,
                displayName: product.displayName || "",
                portionSize: product.portionSize || "Regular",
                channels: formattedChannels,
                totalQuantity: totalQuantity,
                productId: product.productId || "",
                skuId: product.skuId || "",
              };

              cyclePayload.products.push(productPayload);
            }
          },
        );

        if (cyclePayload.products.length > 0) {
          cartAllocation.cycles.push(cyclePayload);
        }
      }

      if (cartAllocation.cycles.length > 0) {
        cartsPayload.push(cartAllocation);
      }
    });

    return {
      restaurantId,
      allocationPlanId,
      date: formattedDate,
      channelAllocationTotals: productWiseChannelAllocations,
      carts: cartsPayload,
    };
  };

  const handleSaveChannelAllocations = async () => {
    if (!apiData?.cartAllocations?.id || !restaurantId || !selectedCycle) {
      toast.error("Missing required data to save allocations");
      return;
    }

    try {
      const payload = formatChannelAllocationPayload();

      if (!payload) {
        toast.error("Failed to format payload");
        return;
      }

      // Calculate total products and channels for success message
      let totalProducts = 0;
      let totalChannels = 0;

      payload.carts.forEach((cart) => {
        cart.cycles.forEach((cycle) => {
          totalProducts += cycle.products.length;
          cycle.products.forEach((product) => {
            totalChannels += Object.keys(product.channels || {}).length;
          });
        });
      });

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const result = await updateChannelAllocation(payload).unwrap();

      // Update initialChannelsRef with current state
      const formattedChannels: Record<
        string,
        Record<string, ChannelBreakdown>
      > = {};
      Object.keys(channels).forEach((sku) => {
        formattedChannels[sku] = {};
        Object.keys(channels[sku] || {}).forEach((cartId) => {
          formattedChannels[sku][cartId] = {};
          Object.keys(channels[sku][cartId] || {}).forEach((channel) => {
            const value = channels[sku][cartId][channel];
            formattedChannels[sku][cartId][channel] = value === 0 ? "" : value;
          });
        });
      });
      initialChannelsRef.current = deepCopyChannels(formattedChannels);

      setSaveSuccess(true);
      setHasUnsavedChanges(false);

      toast.success(
        `All channel allocations saved! (${totalProducts} products, ${totalChannels} channels updated)`,
      );

      // Refresh data
      const formattedDate = formatDateForAPISimple(effectiveSelectedDate);
      fetchCartAllocations({
        restaurantId,
        date: formattedDate,
      });
    } catch (error: any) {
      console.error("Error saving channel allocations:", error);
      toast.error(error.data?.message || "Failed to save channel allocations");
    }
  };

  useEffect(() => {
    if (apiData?.cartAllocations?.carts && selectedCycle && channels) {
      // Set hasUnsavedChanges to true to enable save button
      setHasUnsavedChanges(true);
      setSaveSuccess(false);
    }
  }, [apiData, selectedCycle, channels]);

  const handleSaveAll = () => {
    handleSaveChannelAllocations();
  };

  // Helper function to get tier badge config
  const getTierBadgeConfig = (tier: string) => {
    switch (tier) {
      case "bronze":
        return {
          label: "Brz",
          className: "channel-distribution-tier-badge bronze",
        };
      case "gold":
        return {
          label: "Gld",
          className: "channel-distribution-tier-badge gold",
        };
      case "platinum":
        return {
          label: "Plt",
          className: "channel-distribution-tier-badge platinum",
        };
      default:
        return { label: "", className: "" };
    }
  };

  const filteredProducts = useMemo(() => {
  const products = getProductsForCycle || [];

  return products
    .filter((product) => product.itemType !== "CustomMenu")
    .filter((product) => {
      const matchesSearch =
        product.name.toLowerCase().includes(searchProduct.toLowerCase()) ||
        product.sku.toLowerCase().includes(searchProduct.toLowerCase());

      const matchesTier =
        tierFilter === "all" ||
        product.tier?.toLowerCase() === tierFilter.toLowerCase();

      return matchesSearch && matchesTier;
    });
}, [searchProduct, tierFilter, getProductsForCycle]);

  // Calculate orders before showing confirmation
  const calculateOrdersToCreate = () => {
    if (!apiData?.cartAllocations?.carts || !restaurantId || !selectedCycle) {
      return { cartCount: 0, orderCount: 0 };
    }

    // Check if current cycle is the earliest one
    const earliestCycleId = availableCycles.reduce((earliest, cycle) => {
      if (
        cycle.startTime !== "Invalid Date" &&
        earliest.startTime !== "Invalid Date"
      ) {
        const currentTime = convertTo24Hour(cycle.startTime);
        const earliestTime = convertTo24Hour(earliest.startTime);
        return currentTime < earliestTime ? cycle : earliest;
      }
      return cycle.rawStartSeconds < earliest.rawStartSeconds
        ? cycle
        : earliest;
    }, availableCycles[0]).id;

    const isEarliestCycle = selectedCycle === earliestCycleId;

    const cartChannelMap: Record<
      string,
      Record<
        string,
        {
          cart: ApiCartAllocation;
          orderItems: OrderItem[];
          totalPrice: number;
        }
      >
    > = {};

    (apiData.cartAllocations.carts || []).forEach((cart: ApiCartAllocation) => {
      const cycleAllocation = cart.cycles?.find(
        (c: ApiCycleAllocation) => c.cycleId === selectedCycle,
      );

      // SKIP carts where orders are already placed
      if (!cycleAllocation || cycleAllocation.isOrderPlacedForCycle) return;

      if (cycleAllocation.products) {
        cycleAllocation.products.forEach((product: ApiProductAllocation) => {
          if (!product || !product.itemId) return;

          // SKIP CustomMenu items from order creation
          if (product.itemType === "CustomMenu") return;

          const channelData = channels[product.itemId]?.[cart.cartId];
          if (!channelData || Object.keys(channelData).length === 0) return;

          const unitPrice = getUnitPriceByItemId(product.itemId);

          Object.entries(channelData).forEach(([channelName, allocatedQty]) => {
            if (!allocatedQty || allocatedQty === "" || allocatedQty === 0)
              return;

            const numericQty = allocatedQty === "" ? 0 : Number(allocatedQty);
            if (numericQty <= 0) return;

            const reservedCount = isEarliestCycle
              ? getReservedCount(product.itemId, cart.cartId, channelName)
              : 0;

            const quantityToOrder = Math.max(0, numericQty - reservedCount);

            if (quantityToOrder <= 0) return;

            if (!cartChannelMap[cart.cartId]) {
              cartChannelMap[cart.cartId] = {};
            }

            if (!cartChannelMap[cart.cartId][channelName]) {
              cartChannelMap[cart.cartId][channelName] = {
                cart,
                orderItems: [],
                totalPrice: 0,
              };
            }

            const orderItem: OrderItem = {
              menuItemId: product.itemId,
              displayName: product.displayName || "",
              quantity: quantityToOrder,
              itemType: "MenuItem",
              unitPrice: unitPrice || 0,
            };

            cartChannelMap[cart.cartId][channelName].orderItems.push(orderItem);
            cartChannelMap[cart.cartId][channelName].totalPrice +=
              unitPrice * quantityToOrder;
          });
        });
      }
    });

    let orderCount = 0;
    let cartCount = 0;

    Object.entries(cartChannelMap).forEach(([cartId, channelMap]) => {
      let hasOrders = false;
      Object.entries(channelMap).forEach(([channelName, data]) => {
        if (data.orderItems.length > 0) {
          orderCount++;
          hasOrders = true;
        }
      });
      if (hasOrders) {
        cartCount++;
      }
    });

    return { cartCount, orderCount };
  };

  const handleCreateCartOrdersClick = () => {
    const { cartCount, orderCount } = calculateOrdersToCreate();

    if (orderCount === 0) {
      const isEarliestCycle =
        selectedCycle ===
        availableCycles.reduce((earliest, cycle) => {
          if (
            cycle.startTime !== "Invalid Date" &&
            earliest.startTime !== "Invalid Date"
          ) {
            const currentTime = convertTo24Hour(cycle.startTime);
            const earliestTime = convertTo24Hour(earliest.startTime);
            return currentTime < earliestTime ? cycle : earliest;
          }
          return cycle.rawStartSeconds < earliest.rawStartSeconds
            ? cycle
            : earliest;
        }, availableCycles[0]).id;

      toast.warning(
        isEarliestCycle
          ? "No channel allocations done yet. First do the channel allocation and save before creating orders."
          : "No channel allocations with quantity > 0 found in carts without placed orders",
      );
      return;
    }

    setPendingOrderData({ cartCount, orderCount });
    setShowConfirmationModal(true);
  };

  const handleConfirmCreateOrders = async () => {
    setShowConfirmationModal(false);

    if (!apiData?.cartAllocations?.carts || !restaurantId || !selectedCycle) {
      setOrderCreationStatus({
        type: "error",
        message: "Missing required data to create orders",
        ordersCreated: 0,
      });
      return;
    }

    try {
      // Check if current cycle is the earliest one
      const earliestCycleId = availableCycles.reduce((earliest, cycle) => {
        if (
          cycle.startTime !== "Invalid Date" &&
          earliest.startTime !== "Invalid Date"
        ) {
          const currentTime = convertTo24Hour(cycle.startTime);
          const earliestTime = convertTo24Hour(earliest.startTime);
          return currentTime < earliestTime ? cycle : earliest;
        }
        return cycle.rawStartSeconds < earliest.rawStartSeconds
          ? cycle
          : earliest;
      }, availableCycles[0]).id;

      const isEarliestCycle = selectedCycle === earliestCycleId;

      const orders: OrderRequest[] = [];

      // Group by cartId and channel
      const cartChannelMap: Record<
        string,
        Record<
          string,
          {
            cart: ApiCartAllocation;
            orderItems: OrderItem[];
            totalPrice: number;
          }
        >
      > = {};

      (apiData.cartAllocations.carts || []).forEach(
        (cart: ApiCartAllocation) => {
          const cycleAllocation = cart.cycles?.find(
            (c: ApiCycleAllocation) => c.cycleId === selectedCycle,
          );

          // SKIP carts where orders are already placed
          if (!cycleAllocation || cycleAllocation.isOrderPlacedForCycle) return;

          if (cycleAllocation.products) {
            cycleAllocation.products.forEach(
              (product: ApiProductAllocation) => {
                if (!product || !product.itemId) return;

                // SKIP CustomMenu items from order creation
                if (product.itemType === "CustomMenu") return;

                const channelData = channels[product.itemId]?.[cart.cartId];
                if (!channelData || Object.keys(channelData).length === 0)
                  return;

                const unitPrice = getUnitPriceByItemId(product.itemId);

                Object.entries(channelData).forEach(
                  ([channelName, allocatedQty]) => {
                    if (
                      !allocatedQty ||
                      allocatedQty === "" ||
                      allocatedQty === 0
                    )
                      return;

                    const numericQty =
                      allocatedQty === "" ? 0 : Number(allocatedQty);
                    if (numericQty <= 0) return;

                    const reservedCount = isEarliestCycle
                      ? getReservedCount(
                          product.itemId,
                          cart.cartId,
                          channelName,
                        )
                      : 0;

                    const quantityToOrder = Math.max(
                      0,
                      numericQty - reservedCount,
                    );

                    if (quantityToOrder <= 0) return;

                    if (!cartChannelMap[cart.cartId]) {
                      cartChannelMap[cart.cartId] = {};
                    }

                    if (!cartChannelMap[cart.cartId][channelName]) {
                      cartChannelMap[cart.cartId][channelName] = {
                        cart,
                        orderItems: [],
                        totalPrice: 0,
                      };
                    }

                    const orderItem: OrderItem = {
                      menuItemId: product.itemId,
                      displayName: product.displayName || "",
                      quantity: quantityToOrder,
                      itemType: "MenuItem",
                      unitPrice: unitPrice || 0,
                    };

                    cartChannelMap[cart.cartId][channelName].orderItems.push(
                      orderItem,
                    );
                    cartChannelMap[cart.cartId][channelName].totalPrice +=
                      unitPrice * quantityToOrder;
                  },
                );
              },
            );
          }
        },
      );

      Object.entries(cartChannelMap).forEach(([cartId, channelMap]) => {
        Object.entries(channelMap).forEach(([channelName, data]) => {
          if (data.orderItems.length === 0) return;

          const order: OrderRequest = {
            customerId: "",
            cartId: cartId,
            restaurantId: restaurantId,
            displayName: data.cart.cartName || "",
            address: restaurantDetails?.address || "Restaurant Address",
            phoneNumber: restaurantDetails?.phoneNumber || "",
            imageUrl: restaurantDetails?.imageUrl || "",
            paymentMethod: "CASH",
            orderCreatedBy: "Restaurant",
            deliveryTime: new Date(
              Date.now() + 5 * 60 * 60 * 1000,
            ).toISOString(),
            isScheduledOrder: false,
            isDirectRestaurantOrder: true,
            totalPrice: data.totalPrice,
            orderItems: data.orderItems,
            channel: channelName,
            isCartAllocationOrder: true,
          };

          orders.push(order);
        });
      });

      if (orders.length === 0) {
        setOrderCreationStatus({
          type: "error",
          message: isEarliestCycle
            ? "No quantities available after subtracting pre-orders"
            : "No channel allocations with quantity > 0 found in carts without placed orders",
          ordersCreated: 0,
        });
        return;
      }

      const distributeOrderRequest: DistributeOrderRequest = {
        cycleId: selectedCycle,
        orders: orders,
      };

      let result;
      try {
        result = await distributeOrder(distributeOrderRequest).unwrap();
      } catch (error: any) {
        if (error.status === 200 || error.originalStatus === 200) {
          result = {
            success: true,
            message: "Orders created successfully (200 OK)",
            totalOrdersCreated: orders.length,
          };
        } else {
          throw error;
        }
      }

      const success = result?.success !== false;
      const message = result?.message || "Orders processed successfully";
      const ordersCreated = result?.totalOrdersCreated || orders.length;

      setOrderCreationStatus({
        type: success ? "success" : "error",
        message: message,
        ordersCreated: ordersCreated,
      });

      if (success) {
        setOrderCreationSuccess(true);
        toast.success(`Successfully created ${ordersCreated} orders!`);
      } else {
        setOrderCreationSuccess(false);
        toast.warning(message);
      }

      const formattedDate = formatDateForAPISimple(effectiveSelectedDate);
      fetchCartAllocations({
        restaurantId,
        date: formattedDate,
      });
    } catch (error: any) {
      console.error("Error creating orders:", error);

      let errorMessage = "Failed to create orders";
      if (error.data?.message) {
        errorMessage = error.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.status) {
        errorMessage = `Server returned status ${error.status}`;
      }

      setOrderCreationStatus({
        type: "error",
        message: errorMessage,
        ordersCreated: 0,
      });
      setOrderCreationSuccess(false);
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    if (orderCreationStatus.type) {
      const timer = setTimeout(() => {
        setOrderCreationStatus({ type: null, message: "", ordersCreated: 0 });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [orderCreationStatus]);

  const hasUnsavedChangesInActiveCarts = useMemo(() => {
    if (!hasUnsavedChanges) return false;

    return Object.keys(channels).some((sku) =>
      Object.keys(channels[sku] || {}).some(
        (cartId) => !isOrderPlacedForCart(cartId),
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasUnsavedChanges, channels]);

  const saveButtonEnabled = useMemo(() => {
    // Enable save button when:
    // 1. Not currently saving
    // 2. Has selected cycle
    // 3. Has API data with carts
    // 4. Not in view-only mode for past dates
    return (
      !isSaving &&
      !!selectedCycle &&
      !!apiData?.cartAllocations?.carts &&
      !shouldDisableInputs
    );
  }, [isSaving, selectedCycle, apiData, shouldDisableInputs]);

  // Modified: Create order button enabled based on hasChannelAllocatedCarts instead of saveSuccess
  const createOrderButtonEnabled =
    hasChannelAllocatedCarts &&
    !orderCreationSuccess &&
    !isCreatingOrders &&
    !!selectedCycle &&
    !shouldDisableInputs &&
    isSelectedDateToday(effectiveSelectedDate);

  // Check if current cycle is the earliest one
  const isEarliestCycle = useMemo(() => {
    if (!selectedCycle || !availableCycles.length) return false;

    const earliestCycleId = availableCycles.reduce((earliest, cycle) => {
      if (
        cycle.startTime !== "Invalid Date" &&
        earliest.startTime !== "Invalid Date"
      ) {
        const currentTime = convertTo24Hour(cycle.startTime);
        const earliestTime = convertTo24Hour(earliest.startTime);
        return currentTime < earliestTime ? cycle : earliest;
      }
      return cycle.rawStartSeconds < earliest.rawStartSeconds
        ? cycle
        : earliest;
    }, availableCycles[0]).id;

    return selectedCycle === earliestCycleId;
  }, [selectedCycle, availableCycles]);

  // Helper function to check if error is a 404
  const isNotFoundError = (error: any): boolean => {
    return (
      error?.status === 404 ||
      error?.originalStatus === 404 ||
      (error?.data?.message &&
        error.data.message.toLowerCase().includes("not found")) ||
      (error?.message && error.message.toLowerCase().includes("404")) ||
      (typeof error === "string" && error.toLowerCase().includes("404"))
    );
  };

  const restaurantDetails = useSelector(
    (state: any) => state.restaurant.selectedRestaurantDetails,
  );

  return (
    <>
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast.show}
          onClose={() =>
            setShowToast({ show: false, message: "", variant: "warning" })
          }
          delay={5000}
          autohide
          bg={showToast.variant}
        >
          <Toast.Header>
            <strong className="me-auto">
              {showToast.variant === "warning" ? "Warning" : "Error"}
            </strong>
          </Toast.Header>
          <Toast.Body className="text-white">{showToast.message}</Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Confirmation Modal */}
      <CreateOrdersConfirmationModal
        show={showConfirmationModal}
        onHide={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirmCreateOrders}
        cartCount={pendingOrderData.cartCount}
        orderCount={pendingOrderData.orderCount}
        isCreatingOrders={isCreatingOrders}
      />

      <Container fluid className="cart-channel-distribution-container">
        <Card className="channel-distribution-header-card">
          <Card.Body>
            <div className="channel-distribution-title-section">
              <div>
                <h1 className="channel-distribution-main-title">
                  Channel Breakdown Setup
                  {templateMode && (
                    <Badge color="info" className="ms-2">
                      <Filter size={12} className="me-1" />
                      Template Mode
                    </Badge>
                  )}
                  {singleDateMode && !templateMode && (
                    <Badge color="warning" className="ms-2">
                      <Calendar size={12} className="me-1" />
                      Historical View
                    </Badge>
                  )}
                </h1>
                <div className="channel-distribution-date-info">
                  {templateMode ? (
                    <div className="template-date-info">
                      <small className="text-muted">
                        <Calendar size={12} className="me-1" />
                        Template from: <strong>{templateSourceDate}</strong>
                        <span className="mx-1">→</span>
                        <CalendarDays size={12} className="me-1" />
                        Showing: <strong>{effectiveSelectedDate}</strong>
                        {isShowingLoadFromDate && (
                          <span className="ms-2 text-info">
                            <small>(Showing Load From date)</small>
                          </span>
                        )}
                      </small>
                    </div>
                  ) : singleDateMode ? (
                    <div className="single-date-info">
                      <small className="text-muted">
                        <Calendar size={12} className="me-1" />
                        Showing historical data for:{" "}
                        <strong>{effectiveSelectedDate}</strong>
                      </small>
                    </div>
                  ) : (
                    <div className="current-date-info">
                      <small className="text-muted">
                        <Calendar size={12} className="me-1" />
                        Showing data for:{" "}
                        <strong>{effectiveSelectedDate}</strong>
                        {isEarliestCycle && (
                          <span className="ms-2 text-info">
                            <small>
                              (Showing reserved counts for earliest cycle)
                            </small>
                          </span>
                        )}
                      </small>
                    </div>
                  )}
                </div>
              </div>
              <div className="channel-distribution-header-actions">
                <Button
                  variant={
                    saveSuccess ? "success" : templateMode ? "info" : "primary"
                  }
                  onClick={handleSaveAll}
                  className="channel-distribution-save-button"
                  disabled={!saveButtonEnabled || shouldDisableInputs}
                  title={
                    shouldDisableInputs
                      ? "Cannot modify past dates"
                      : templateMode
                        ? `Save allocations`
                        : singleDateMode
                          ? `Save allocations`
                          : `Save allocations`
                  }
                >
                  {shouldDisableInputs ? (
                    <>
                      <Eye size={16} className="me-2" />
                      View Only
                    </>
                  ) : isSaving ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        className="me-2"
                      />
                      Confirming...
                    </>
                  ) : saveSuccess ? (
                    <>
                      <Save size={16} className="me-2" />✓ Confirmed
                    </>
                  ) : (
                    <>
                      <Save size={16} className="me-2" />
                      Confirm {hasUnsavedChangesInActiveCarts && "•"}
                    </>
                  )}
                </Button>

                {!templateMode &&
                  !singleDateMode &&
                  isSelectedDateToday(effectiveSelectedDate) && (
                    <Button
                      variant={orderCreationSuccess ? "success" : "secondary"}
                      onClick={handleCreateCartOrdersClick}
                      className="channel-distribution-create-order-button ms-2"
                      disabled={!createOrderButtonEnabled}
                      title={
                        !hasChannelAllocatedCarts
                          ? "No carts with channel allocations found"
                          : hasUnsavedChanges
                            ? "Please save changes before creating orders"
                            : ""
                      }
                    >
                      {isCreatingOrders ? (
                        <>
                          <Spinner
                            as="span"
                            animation="border"
                            size="sm"
                            className="me-2"
                          />
                          Creating...
                        </>
                      ) : orderCreationSuccess ? (
                        <>
                          <Package size={16} className="me-2" />✓ Orders Created
                        </>
                      ) : (
                        <>
                          <Package size={16} className="me-2" />
                          Create Cart Orders
                        </>
                      )}
                    </Button>
                  )}
              </div>
            </div>

            <div className="channel-distribution-filters-container">
              <div className="channel-distribution-search-wrapper">
                <Form.Control
                  type="text"
                  placeholder="Search products..."
                  value={searchProduct}
                  onChange={(e) => setSearchProduct(e.target.value)}
                  className="channel-distribution-search-input"
                />
              </div>
              <div className="channel-distribution-cycle-selector">
                <Form.Select
                  value={selectedCycle || ""}
                  onChange={(e) => handleCycleChange(e.target.value)}
                  className="channel-distribution-cycle-select"
                  disabled={availableCycles.length === 0 || isLoading}
                >
                  {availableCycles.length === 0 ? (
                    <option value="">No cycles available</option>
                  ) : (
                    availableCycles.map((cycle) => {
                      const timeDisplay =
                        cycle.startTime === "Invalid Date"
                          ? `${cycle.rawStartSeconds}s - ${cycle.rawEndSeconds}s`
                          : `${cycle.startTime} - ${cycle.endTime}`;

                      return (
                        <option key={cycle.id} value={cycle.id}>
                          {cycle.name} ({timeDisplay})
                          {cycle.id === selectedCycle && isEarliestCycle}
                        </option>
                      );
                    })
                  )}
                </Form.Select>
              </div>
              <div className="channel-distribution-tier-selector">
                <Form.Select
                  value={tierFilter}
                  onChange={(e) => setTierFilter(e.target.value)}
                  className="channel-distribution-tier-select"
                  disabled={isLoading}
                >
                  <option value="all">All Tiers</option>
                  <option value="bronze">Bronze</option>
                  <option value="gold">Gold</option>
                  <option value="platinum">Platinum</option>
                </Form.Select>
              </div>
            </div>
          </Card.Body>
        </Card>

        <Card className="channel-distribution-grid-card">
          <Card.Body className="channel-distribution-grid-content">
            {isLoading ? (
              <div className="channel-breakdown-grid-loading">
                <LoadingMessage />
              </div>
            ) : isError ? (
              <div className="grid-error-container">
                {isNotFoundError(error) ? (
                  <ChannelNotFoundError
                    date={effectiveSelectedDate}
                    isTemplateMode={templateMode}
                    templateSourceDate={templateSourceDate}
                    onRetry={() => {
                      if (restaurantId && effectiveSelectedDate) {
                        const formattedDate = formatDateForAPISimple(
                          effectiveSelectedDate,
                        );
                        fetchCartAllocations({
                          restaurantId,
                          date: formattedDate,
                        });
                      }
                    }}
                  />
                ) : (
                  <ErrorMessageComponent />
                )}
              </div>
            ) : (
              <div
                className="channel-distribution-grid-wrapper"
                ref={gridWrapperRef}
              >
                <div className="channel-distribution-header-table channel-distribution-header-in-scroll">
                  <div className="channel-distribution-table-cell channel-distribution-product-header-cell">
                    <Package size={16} />
                    <span>Product (SKU)</span>
                  </div>

                  <div className="channel-distribution-cart-headers-container">
                    {getCartsForCycle.map((cart) => (
                      <div
                        key={cart.id}
                        className="channel-distribution-table-cell channel-distribution-cart-header-cell"
                      >
                        <div className="channel-distribution-cart-name">
                          {cart.name}
                          {cart.isOrderPlaced && (
                            <Badge bg="success" className="ms-2">
                              Orders Placed
                            </Badge>
                          )}
                        </div>
                        <Badge className="channel-distribution-cart-badge">
                          {cart.type === "fixed"
                            ? "Fixed"
                            : cart.type === "hq"
                              ? "HQ"
                              : "Delivery"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="channel-distribution-table-body">
                  {filteredProducts.length === 0 ? (
                    <div className="text-center py-4">
                      No products found for the selected cycle and filters.
                    </div>
                  ) : (
                    filteredProducts.map((product) => {
                      // eslint-disable-next-line @typescript-eslint/no-unused-vars
                      const tierBadge = getTierBadgeConfig(product.tier);
                      const isCustomMenu = product.itemType === "CustomMenu";

                      return (
                        <div
                          key={product.sku}
                          className={`channel-distribution-table-row ${
                            isCustomMenu ? "custom-menu-row" : ""
                          }`}
                        >
                          <div className="channel-distribution-table-cell channel-distribution-product-cell">
                            <div className="channel-distribution-product-info">
                              <h6 className="channel-distribution-product-name">
                                {product.name}
                                {isCustomMenu && (
                                  <Badge bg="secondary" className="ms-2">
                                    Custom Menu
                                  </Badge>
                                )}
                              </h6>
                             
                            </div>
                          </div>

                          <div className="channel-distribution-cart-cells-container">
                            {getCartsForCycle.map((cart) => {
                              const allocated = getAllocatedAmount(
                                product.sku,
                                cart.id,
                              );
                              const channelTotal = getChannelTotal(
                                product.sku,
                                cart.id,
                              );
                              const valid = isValidAllocation(
                                product.sku,
                                cart.id,
                              );

                              const cartHeader = apiData?.cartsHeader?.find(
                                (c: ApiCartHeader) => c.id === cart.id,
                              );
                              const supportedChannels =
                                cartHeader?.supportedChannels || [];

                              const currentChannels =
                                channels[product.sku]?.[cart.id] || {};

                              const cellClass = `channel-distribution-table-cell channel-distribution-cart-cell ${
                                allocated === 0
                                  ? "no-allocation"
                                  : valid
                                    ? "valid"
                                    : "invalid"
                              } ${cart.isOrderPlaced ? "orders-placed" : ""} ${
                                isCustomMenu ? "custom-menu-cell" : ""
                              }`;

                              return (
                                <div
                                  key={`${product.sku}-${cart.id}`}
                                  className={cellClass}
                                >
                                  <div className="channel-distribution-allocation-container">
                                    <div className="channel-distribution-total-row">
                                      <span className="channel-distribution-total-label">
                                        Total:
                                      </span>
                                      <div className="channel-distribution-total-display">
                                        <span
                                          className={`channel-distribution-current-total ${
                                            allocated === 0
                                              ? "no-allocation"
                                              : valid
                                                ? "valid"
                                                : "invalid"
                                          }`}
                                        >
                                          {channelTotal}
                                        </span>
                                        <span className="channel-distribution-allocated-total">
                                          / {allocated}
                                        </span>
                                      </div>
                                    </div>

                                    <div className="channel-distribution-content-area">
                                      {allocated === 0 ? (
                                        <div className="channel-distribution-no-allocation-message">
                                          <Clock size={16} />
                                          <span>
                                            No allocation for this cycle
                                          </span>
                                        </div>
                                      ) : supportedChannels.length === 0 ? (
                                        <div className="channel-distribution-no-channels-message">
                                          <Package size={16} />
                                          <span>No channels configured</span>
                                        </div>
                                      ) : (
                                        <div className="channel-distribution-channels-container">
                                          {supportedChannels.map(
                                            (channel: string) => {
                                              const standardizedChannel =
                                                standardizeChannelName(channel);
                                              const isDisabled =
                                                cart.isOrderPlaced ||
                                                isCustomMenu ||
                                                shouldDisableInputs;

                                              // Get reserved count for this channel (only for earliest cycle)
                                              const reservedCount =
                                                getReservedCount(
                                                  product.sku,
                                                  cart.id,
                                                  standardizedChannel,
                                                );

                                              return (
                                                <div
                                                  key={standardizedChannel}
                                                  className="channel-distribution-channel-row"
                                                >
                                                  <div className="channel-distribution-channel-header">
                                                    <label className="channel-distribution-channel-label">
                                                      {getChannelDisplayName(
                                                        standardizedChannel,
                                                      )}
                                                      :
                                                    </label>
                                                    {reservedCount > 0 && (
                                                      <span className="channel-distribution-reserved-count">
                                                        ({reservedCount})
                                                      </span>
                                                    )}
                                                  </div>
                                                  <Form.Control
                                                    type="number"
                                                    min="0"
                                                    max={allocated}
                                                    value={
                                                      currentChannels[
                                                        standardizedChannel
                                                      ] === "" ||
                                                      currentChannels[
                                                        standardizedChannel
                                                      ] === 0
                                                        ? ""
                                                        : currentChannels[
                                                            standardizedChannel
                                                          ] || ""
                                                    }
                                                    onChange={(e) => {
                                                      if (
                                                        shouldDisableInputs ||
                                                        isDisabled ||
                                                        isCustomMenu
                                                      )
                                                        return; // Prevent changes for CustomMenu
                                                      const val =
                                                        e.target.value;
                                                      if (val === "") {
                                                        updateChannel(
                                                          product.sku,
                                                          cart.id,
                                                          channel,
                                                          "",
                                                        );
                                                      } else {
                                                        const numValue =
                                                          parseInt(val) || 0;
                                                        updateChannel(
                                                          product.sku,
                                                          cart.id,
                                                          channel,
                                                          numValue,
                                                        );
                                                      }
                                                    }}
                                                    placeholder="0"
                                                    className="channel-distribution-channel-inputs"
                                                    size="sm"
                                                    disabled={
                                                      isDisabled ||
                                                      shouldDisableInputs ||
                                                      isCustomMenu
                                                    }
                                                    onWheel={(e) =>
                                                      e.currentTarget.blur()
                                                    }
                                                    title={
                                                      shouldDisableInputs
                                                        ? "Cannot modify past dates"
                                                        : isCustomMenu
                                                          ? "Custom Menu items cannot be modified"
                                                          : isDisabled
                                                            ? "Orders already placed for this cart"
                                                            : ""
                                                    }
                                                    style={
                                                      isCustomMenu
                                                        ? {
                                                            backgroundColor:
                                                              "#f5f5f5",
                                                            cursor:
                                                              "not-allowed",
                                                            opacity: 0.8,
                                                          }
                                                        : {}
                                                    }
                                                  />
                                                </div>
                                              );
                                            },
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </Card.Body>
        </Card>
      </Container>
    </>
  );
};

export default ChannelBreakDown;
